import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Package, ArrowRight, AlertTriangle, CheckCircle2, X } from 'lucide-react';
import { API } from '../context/AdminAuthContext';
import { useToast } from '../hooks/use-toast';
import {
  AlertDialog, AlertDialogContent,
} from './ui/alert-dialog';

/**
 * PickQtyModal — mobile-first modal that lets a picker enter the
 * actual quantity they picked from the pallet backing a picking task.
 *
 * Contract with backend:
 *  * `GET  /admin/warehouse/tasks/{id}/pallet-availability` → live
 *     `pallet_available` + `required_qty` shown side-by-side.
 *  * `POST /admin/warehouse/tasks/{id}/pick { picked_qty }`     →
 *     completes this task, adjusts allocation, cascades shortfall.
 *
 * UX rules:
 *  * Big numeric keypad-friendly input (`inputMode="numeric"`).
 *  * Ceiling = `min(required, available)`. The input `max` is enforced
 *     both client-side (button greyed) and server-side.
 *  * "Take all" quick button = ceiling; "Half" and "None+cascade" for
 *     common short-pick patterns; short-picking auto-warns the picker.
 *  * On success, cascade tasks are surfaced in a green banner so the
 *     picker knows a new card will appear in their queue.
 */
const PickQtyModal = ({ open, task, authHeaders, onClose, onCompleted }) => {
  const { toast } = useToast();
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pickedQty, setPickedQty] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { picked_qty, shortfall_qty, cascade_tasks }

  const required = Number(task?.qty || 0);
  const available = Number(avail?.pallet_available ?? 0);
  const ceiling = Math.min(required, available);
  const numericQty = Number(pickedQty);
  const isValidQty = Number.isInteger(numericQty) && numericQty >= 1 && numericQty <= ceiling;

  useEffect(() => {
    if (!open || !task?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setAvail(null);
      setPickedQty('');
      setResult(null);
      try {
        const r = await axios.get(
          `${API}/admin/warehouse/tasks/${task.id}/pallet-availability`,
          { headers: authHeaders },
        );
        if (!cancelled) setAvail(r.data);
      } catch (e) {
        if (!cancelled) toast({ title: 'Could not load pallet availability', description: e?.response?.data?.detail || e?.message });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, task?.id, authHeaders, toast]);

  const submit = async () => {
    if (!isValidQty) return;
    setSubmitting(true);
    try {
      const r = await axios.post(
        `${API}/admin/warehouse/tasks/${task.id}/pick`,
        { picked_qty: numericQty },
        { headers: authHeaders },
      );
      setResult(r.data);
      try { navigator.vibrate?.(40); } catch { /* noop */ }
      if (typeof onCompleted === 'function') onCompleted(r.data);
    } catch (e) {
      toast({ title: 'Pick failed', description: e?.response?.data?.detail || e?.message });
    } finally {
      setSubmitting(false);
    }
  };

  const shortfall = required - numericQty;

  return (
    <AlertDialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <AlertDialogContent className="max-w-md w-[92vw] p-0 rounded-3xl overflow-hidden" data-testid="pick-qty-modal">
        {/* Header */}
        <div className="p-5 bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
              <Package className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">Confirm pick</div>
              <div className="font-display text-lg leading-tight" data-testid="pick-qty-product-name">
                {task?.product_name || '—'}
              </div>
              <div className="text-[11px] opacity-90 mt-1 font-mono">
                {task?.pallet_id || '—'} · {task?.zone || '—'}/{task?.location_id || '—'}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center flex-shrink-0"
              data-testid="pick-qty-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Body */}
        {result ? (
          // Success screen — shows what was picked + any cascade tasks
          // the server spawned for the shortfall.
          <div className="p-6 text-center">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-3">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="font-display text-xl text-slate-900" data-testid="pick-qty-success">Pick confirmed</div>
            <div className="text-slate-600 text-sm mt-1">
              Picked <span className="font-bold text-emerald-700">{result.picked_qty}</span> {task?.uom || 'ctn'}
            </div>
            {Number(result.shortfall_qty) > 0 && (
              <div className="mt-4 rounded-xl bg-amber-50 border border-amber-200 p-3 text-left" data-testid="pick-qty-cascade-banner">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[13px] font-bold text-amber-900">
                      Short by {result.shortfall_qty} {task?.uom || 'ctn'}
                    </div>
                    <div className="text-[11px] text-amber-800 mt-1 leading-relaxed">
                      {Array.isArray(result.cascade_tasks) && result.cascade_tasks.length > 0
                        ? `${result.cascade_tasks.length} new pick task${result.cascade_tasks.length === 1 ? '' : 's'} added to your queue:`
                        : 'No eligible pallets — logged as a shortfall for the manager.'}
                    </div>
                    {(result.cascade_tasks || []).map((c) => (
                      <div key={c.id} className="text-[11px] font-mono text-amber-900 mt-1" data-testid={`pick-qty-cascade-${c.id}`}>
                        · {c.pallet_id || '—'} · {c.qty} {task?.uom || 'ctn'}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="mt-5 w-full h-12 rounded-xl bg-slate-950 hover:bg-black text-white font-bold text-sm"
              data-testid="pick-qty-done"
            >
              Done
            </button>
          </div>
        ) : loading ? (
          <div className="p-12 flex justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="p-5">
            {/* Required / Available tiles side-by-side */}
            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3 text-center" data-testid="pick-qty-required">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Required</div>
                <div className="text-3xl font-bold text-slate-900 tabular-nums mt-1 leading-none">{required}</div>
                <div className="text-[10px] text-slate-500 mt-1 uppercase">{task?.uom || 'ctn'}</div>
              </div>
              <div className={`rounded-2xl border p-3 text-center ${available < required ? 'bg-amber-50 border-amber-300' : 'bg-emerald-50 border-emerald-200'}`} data-testid="pick-qty-available">
                <div className={`text-[10px] uppercase tracking-wider font-bold ${available < required ? 'text-amber-700' : 'text-emerald-700'}`}>Available on pallet</div>
                <div className={`text-3xl font-bold tabular-nums mt-1 leading-none ${available < required ? 'text-amber-900' : 'text-emerald-900'}`}>{available}</div>
                <div className={`text-[10px] mt-1 uppercase ${available < required ? 'text-amber-700' : 'text-emerald-700'}`}>{task?.uom || 'ctn'}</div>
              </div>
            </div>

            {/* Numeric input */}
            <div className="mb-3">
              <label className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 block">Picked quantity</label>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={ceiling}
                value={pickedQty}
                onChange={(e) => setPickedQty(e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="0"
                className="w-full h-16 text-center text-4xl font-bold tabular-nums rounded-2xl border-2 border-slate-200 focus:border-amber-500 focus:outline-none bg-white"
                data-testid="pick-qty-input"
                autoFocus
              />
              <div className="mt-1.5 text-[11px] text-slate-500 text-center">
                Enter 1 to {ceiling} {task?.uom || 'ctn'}
              </div>
            </div>

            {/* Quick-select buttons */}
            <div className="grid grid-cols-3 gap-2 mb-4">
              <button
                type="button"
                onClick={() => setPickedQty(String(ceiling))}
                disabled={ceiling < 1}
                className="h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="pick-qty-take-all"
              >
                All {ceiling}
              </button>
              <button
                type="button"
                onClick={() => setPickedQty(String(Math.max(1, Math.floor(ceiling / 2))))}
                disabled={ceiling < 2}
                className="h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="pick-qty-half"
              >
                Half {Math.max(1, Math.floor(ceiling / 2))}
              </button>
              <button
                type="button"
                onClick={() => setPickedQty('')}
                className="h-11 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-bold text-xs"
                data-testid="pick-qty-clear"
              >
                Clear
              </button>
            </div>

            {/* Shortfall preview */}
            {isValidQty && shortfall > 0 && (
              <div className="mb-4 rounded-xl bg-amber-50 border border-amber-200 p-3" data-testid="pick-qty-shortfall-preview">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="text-[12px] text-amber-900 leading-relaxed">
                    Shortfall of <span className="font-bold">{shortfall}</span> {task?.uom || 'ctn'} will cascade to the next FEFO pallet automatically.
                  </div>
                </div>
              </div>
            )}

            {/* Submit */}
            <button
              type="button"
              onClick={submit}
              disabled={!isValidQty || submitting}
              className="w-full h-14 rounded-2xl bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold text-base inline-flex items-center justify-center gap-2 transition-colors"
              data-testid="pick-qty-submit"
            >
              {submitting
                ? <><Loader2 className="w-5 h-5 animate-spin" /> Confirming…</>
                : <>Confirm pick <ArrowRight className="w-5 h-5" /></>}
            </button>
          </div>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default PickQtyModal;
